import { useAsyncData, useTryAsyncData } from 'client/lib/hooks';
import { RpxResponse, rpx } from 'client/lib/rpx-client';
import { DefaultSpinner } from '@components/spinner';
import { PurchasePane } from '../pmts/components/purchase-pane';
import { CorePricingTable } from '../pmts/components/core-pricing-table';
import { useState } from 'preact/hooks';
import { useCurrentTenant, useCurrentUser } from '@components/router/session-context';
import { AccountTabContent } from '@components/password-form';
import { useImageUrl } from 'client/utils/cdn';
import * as dtfmt from 'shared/dateutil';
import { PriceSummary } from '../pmts/components/price-summary';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { BtnSecondary } from '@components/buttons';
import { showError } from '@components/app-error';
import { useRouteParams, useRouter } from '@components/router';
import { SlideOver } from '@components/slide-over';
import { PaymentPane } from '../student-course-preferences';
import { hasLevel } from 'shared/auth';
import { IcoChevronDown, IcoChevronRight, IcoCreditCard, IcoRuzukuLogo } from '@components/icons';
import { useIntl } from 'shared/intl/use-intl';
import { AipBillingCard } from './aip-billing-card';

type PurchaseState = {
  isInitializing: boolean;
  isLoading: boolean;
  cursor?: number;
  isAipMember: boolean;
  purchases: RpxResponse<typeof rpx.accounts.getMyProductPurchases>['purchases'];
};

function CourseImage({ src }: { src: undefined | string }) {
  const imageUrl = useImageUrl(src);
  return (
    <span class="flex items-center justify-center text-center w-12 min-w-12 h-12 bg-gray-100 border-b overflow-hidden rounded-sm shadow-sm">
      {imageUrl && <img src={imageUrl} loading="lazy" class="object-cover" />}
      {!imageUrl && <span class="opacity-50 text-xs"></span>}
    </span>
  );
}

type PurchaseItem = PurchaseState['purchases'][0];

function isFreePrice(price?: PurchaseItem['price']) {
  return price?.paymentType === 'free';
}

function CoursePurchaseItem({ purchase, price, coupon }: PurchaseItem) {
  if (!price) {
    return null;
  }

  return (
    <a
      href={`?courseId=${purchase.courseId}`}
      class="rounded-sm flex items-center gap-4 p-2 text-inherit hover:bg-gray-50"
    >
      <CourseImage src={purchase.imagePath} />
      <span class="flex flex-col">
        <strong>{purchase.courseTitle}</strong>
        <span class="flex items-center gap-2 text-gray-500">
          <span>{dtfmt.compactDate(purchase.createdAt)}</span>
          {isFreePrice(price) ? (
            <>
              {' • '}
              <span class="text-emerald-700">Free — no charge</span>
            </>
          ) : (
            <>
              {' • '}
              <span>
                <PriceSummary price={price} coupon={coupon} multiline={false} />
              </span>
              {' • '}
              <span class={purchase.status === 'active' ? 'text-green-600' : 'text-gray-500'}>
                {purchase.status}
              </span>
            </>
          )}
        </span>
      </span>
    </a>
  );
}

function BundleGroup({ bundle, items }: { bundle: PurchaseItem; items: PurchaseItem[] }) {
  const [isExpanded, setIsExpanded] = useState(true);
  if (!bundle.price) {
    return null;
  }
  return (
    <div class="flex flex-col">
      <div class="flex items-center gap-2 rounded-sm hover:bg-gray-50">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          class="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-700"
          aria-label={isExpanded ? 'Collapse bundle' : 'Expand bundle'}
          aria-expanded={isExpanded}
        >
          {isExpanded ? <IcoChevronDown class="w-4 h-4" /> : <IcoChevronRight class="w-4 h-4" />}
        </button>
        <a
          href={`?courseId=${bundle.purchase.courseId}`}
          class="rounded-sm flex flex-1 items-center gap-4 p-2 text-inherit"
        >
          <CourseImage src={bundle.purchase.imagePath} />
          <span class="flex flex-col">
            <strong>{bundle.purchase.courseTitle}</strong>
            <span class="flex items-center gap-2 text-gray-500">
              <span>{dtfmt.compactDate(bundle.purchase.createdAt)}</span>
              {isFreePrice(bundle.price) ? (
                <>
                  {' • '}
                  <span class="text-emerald-700">Free — no charge</span>
                </>
              ) : (
                <>
                  {' • '}
                  <span>
                    <PriceSummary price={bundle.price} coupon={bundle.coupon} multiline={false} />
                  </span>
                  {' • '}
                  <span
                    class={bundle.purchase.status === 'active' ? 'text-green-600' : 'text-gray-500'}
                  >
                    {bundle.purchase.status}
                  </span>
                </>
              )}
            </span>
            <span class="text-xs text-gray-500 mt-0.5">
              Includes {items.length} {items.length === 1 ? 'course' : 'courses'}
            </span>
          </span>
        </a>
        {bundle.purchase.status === 'active' && !isFreePrice(bundle.price) && (
          <a
            href={`?courseId=${bundle.purchase.courseId}`}
            class="text-sm text-indigo-600 hover:underline whitespace-nowrap pr-3"
          >
            Manage billing
          </a>
        )}
      </div>
      {isExpanded && items.length > 0 && (
        <div class="flex flex-col pl-8 border-l border-gray-200 ml-3 mt-1 gap-1">
          {items.map((child) => (
            <a
              key={child.purchase.id + ':' + child.purchase.courseId}
              href={`?courseId=${child.purchase.courseId}`}
              class="rounded-sm flex items-center gap-3 p-1.5 text-inherit text-sm hover:bg-gray-50"
            >
              <CourseImage src={child.purchase.imagePath} />
              <span class="text-gray-700">{child.purchase.courseTitle}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

type PurchaseGroup =
  | { kind: 'bundle'; bundle: PurchaseItem; children: PurchaseItem[]; sortAt: string }
  | { kind: 'single'; item: PurchaseItem; sortAt: string };

function groupPurchases(purchases: PurchaseItem[]): PurchaseGroup[] {
  const byPurchaseId = new Map<string, PurchaseItem[]>();
  for (const p of purchases) {
    const id = p.purchase.id;
    const existing = byPurchaseId.get(id);
    if (existing) {
      existing.push(p);
    } else {
      byPurchaseId.set(id, [p]);
    }
  }
  const groups: PurchaseGroup[] = [];
  for (const items of byPurchaseId.values()) {
    const bundle = items.find((i) => i.purchase.isBundle);
    if (bundle && items.length > 1) {
      const children = items.filter((i) => i !== bundle);
      groups.push({
        kind: 'bundle',
        bundle,
        children,
        sortAt: String(bundle.purchase.createdAt),
      });
    } else {
      for (const item of items) {
        groups.push({ kind: 'single', item, sortAt: String(item.purchase.createdAt) });
      }
    }
  }
  groups.sort((a, b) => (a.sortAt < b.sortAt ? 1 : a.sortAt > b.sortAt ? -1 : 0));
  return groups;
}

function CoursePurchaseDetails({ courseId }: { courseId: string }) {
  const course = useTryAsyncData(
    () => rpx.courses.getPurchasedCourse({ id: courseId }),
    [courseId],
  );
  const router = useRouter();
  return (
    <SlideOver close={() => router.goto(location.pathname)}>
      {course.data && (
        <header class="flex items-center gap-4">
          <CourseImage src={course.data?.imagePath} />
          <strong>{course.data.title}</strong>
        </header>
      )}
      <PaymentPane courseId={courseId} />
    </SlideOver>
  );
}

function RuzukuBillingItem() {
  const user = useCurrentUser();
  return (
    <>
      <h2 class="font-semibold">Your Ruzuku Account</h2>
      <a
        href={`?coreBilling=true`}
        class="rounded-sm flex items-center gap-4 p-2 text-inherit hover:bg-gray-50"
      >
        <span class="flex items-center justify-center text-center w-12 min-w-12 h-12 bg-gray-100 border-b overflow-hidden rounded-sm shadow-sm p-2">
          <IcoRuzukuLogo />
        </span>
        <span class="flex flex-col">
          <strong>Manage your Ruzuku billing</strong>
          <span class="flex items-center text-gray-500 capitalize">Tier: {user?.tier}</span>
        </span>
      </a>
    </>
  );
}

export function BillingTab() {
  const intl = useIntl();
  const user = useCurrentUser();
  const tenant = useCurrentTenant();
  const displayBilling = hasLevel(user, tenant.isCore ? 'guide' : 'admin');
  const { courseId, coreBilling } = useRouteParams();
  const [state, setState] = useState<PurchaseState>(() => ({
    isLoading: true,
    isInitializing: true,
    isAipMember: false,
    purchases: [],
  }));

  useAsyncEffect(async () => {
    const productPurchases = await rpx.accounts.getMyProductPurchases({ cursor: 0 });
    setState({
      isInitializing: false,
      isLoading: false,
      ...productPurchases,
    });
  }, []);

  const loadMore = async () => {
    try {
      setState((s) => ({ ...s, isLoading: true }));
      const result = await rpx.accounts.getMyProductPurchases({ cursor: state.cursor || 0 });
      setState((s) => ({
        ...s,
        cursor: result.cursor,
        purchases: [...s.purchases, ...result.purchases],
      }));
    } catch (err) {
      showError(err);
    } finally {
      setState((s) => ({ ...s, isLoading: false }));
    }
  };

  if (state.isInitializing) {
    return (
      <div class="p-16">
        <DefaultSpinner />
      </div>
    );
  }

  return (
    <div class="flex flex-col gap-4 py-8">
      {courseId && <CoursePurchaseDetails courseId={courseId} />}
      {coreBilling && <RuzukuBillingDetails />}
      {displayBilling && <RuzukuBillingItem />}
      {state.isAipMember && <AipBillingCard />}

      {state.purchases.length > 0 && (
        <>
          <h2 class="px-2 font-semibold">{intl('Your Purchases')}</h2>
          {groupPurchases(state.purchases).map((group) =>
            group.kind === 'bundle' ? (
              <BundleGroup
                key={group.bundle.purchase.id}
                bundle={group.bundle}
                items={group.children}
              />
            ) : (
              <CoursePurchaseItem
                {...group.item}
                key={group.item.purchase.id + ':' + group.item.purchase.courseId}
              />
            ),
          )}
          {!!state.cursor && (
            <BtnSecondary isLoading={state.isLoading} onClick={loadMore}>
              Load More
            </BtnSecondary>
          )}
        </>
      )}
      {!displayBilling && state.purchases.length === 0 && (
        <div class="flex flex-col items-center justify-center py-20 text-center">
          <span class="flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 text-indigo-400 mb-4">
            <IcoCreditCard class="w-8 h-8" />
          </span>
          <p class="text-lg font-medium text-gray-700">{intl('No billing history')}</p>
          <p class="text-sm text-gray-400 mt-1 max-w-xs">
            {intl('Your purchases and subscriptions will appear here.')}
          </p>
          <p class="text-sm text-gray-400 mt-4">
            {intl('Questions? Contact us at')}{' '}
            <a href="mailto:support@mirasee.com" class="text-indigo-500 hover:underline">
              support@mirasee.com
            </a>
          </p>
        </div>
      )}
    </div>
  );
}

function RuzukuBillingDetails() {
  const user = useCurrentUser();
  const router = useRouter();

  return (
    <SlideOver close={() => router.goto(location.pathname)}>
      {user?.tier === 'free' && <CorePricingTable />}
      {user?.tier !== 'free' && <NonFreeContent />}
    </SlideOver>
  );
}

function NonFreeContent() {
  const { isLoading, data } = useAsyncData(() => rpx.myCorePayments.getCorePurchases(), []);
  const [showPricing, setShowPricing] = useState(false);

  if (isLoading || !data) {
    return <DefaultSpinner />;
  }

  const { isBilledExternally, corePurchase, instantCoursePurchase, account } = data;
  const isLifetime = account.isLifetime || !!data.corePurchase?.price?.metadata?.isLifetime;

  if (isBilledExternally || (!corePurchase && !instantCoursePurchase)) {
    return (
      <AccountTabContent title="Ruzuku Billing">
        <p class="mb-8">
          Contact <a href="mailto:billing@ruzuku.com">billing@ruzuku.com</a> with any questions
          about your account.
        </p>
        {!isBilledExternally && !isLifetime && <CorePricingTable />}
      </AccountTabContent>
    );
  }

  return (
    <AccountTabContent title="Ruzuku Billing">
      {showPricing && (
        <div class="my-8">
          <CorePricingTable />
        </div>
      )}
      <PurchasePane
        {...corePurchase}
        allowCancel={!isLifetime && corePurchase?.price?.paymentType !== 'paymentplan'}
        changePlan={isLifetime ? undefined : () => setShowPricing(true)}
        changeMessage={
          isLifetime && (
            <span>
              Contact{' '}
              <a href="mailto:billing@ruzuku.com" class="text-indigo-600 underline">
                billing
              </a>{' '}
              to change your Ruzuku plan.
            </span>
          )
        }
        asSeller={false}
      />
      {corePurchase && instantCoursePurchase && <hr class="my-8" />}
      <PurchasePane
        {...instantCoursePurchase}
        allowCancel={instantCoursePurchase?.price?.paymentType !== 'paymentplan'}
        changePlan={() => setShowPricing(true)}
        asSeller={false}
      />
    </AccountTabContent>
  );
}
